// ClinicsTableTablet.jsx
import Table, {
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableHeaderCell,
} from "@components/ui/Table";
import Button from "@components/ui/Button";
import Truncate from "@components/ui/Truncate";
import { isPmsConfigured } from "./SchedulingEditor";

function StatusPill({ enabled }) {
  return (
    <span
      className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
        enabled ? "bg-green-100 text-green-800" : "bg-gray-100 text-gray-700"
      }`}
    >
      {enabled ? "Enabled" : "Disabled"}
    </span>
  );
}

function RatingBadge({ stats }) {
  if (!stats || stats.total_rated === 0) {
    return null;
  }

  const score = stats.average_score;
  const getScoreColor = (score) => {
    if (score >= 4) return "bg-green-100 text-green-700";
    if (score >= 3) return "bg-yellow-100 text-yellow-700";
    return "bg-red-100 text-red-700";
  };

  return (
    <div className="mt-2">
      <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${getScoreColor(score)}`}>
        Rating: {score.toFixed(1)}/5 ({stats.total_rated})
      </span>
      {stats.needs_review > 0 && (
        <span className="ml-1 text-xs text-orange-600">
          ({stats.needs_review} review)
        </span>
      )}
    </div>
  );
}

export default function ClinicsTableTablet({ clinics, onEdit, onView, onImpersonate, onRunSetup, setupLoading, onReport }) {
  return (
    <Table>
      <TableHead>
        <TableRow>
          <TableHeaderCell className="w-[30%]">Clinic</TableHeaderCell>
          <TableHeaderCell className="w-[16%]">Phone</TableHeaderCell>
          <TableHeaderCell className="w-[20%]">UniFi</TableHeaderCell>
          <TableHeaderCell className="w-[24%]">Quick View</TableHeaderCell>
          <TableHeaderCell className="w-[10%]">Actions</TableHeaderCell>
        </TableRow>
      </TableHead>

      <TableBody>
        {clinics.map((c) => {
          const inactive = c.is_active === false;
          return (
          <TableRow
            key={c.id}
            className={`hover:bg-gray-50 ${inactive ? "opacity-60" : ""}`}
          >
            <TableCell className="align-top">
              <div className="flex items-center gap-2 mb-1 flex-wrap">
                <div className="font-medium text-sm">
                  {c.clinic_name || "—"}
                </div>
                {inactive && (
                  <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-700">
                    Inactive
                  </span>
                )}
              </div>
              <div className="text-xs text-gray-500 break-all truncate">
                {c.id}
              </div>
              <div className="text-xs mt-1 text-gray-700">
                <span className="font-medium">TZ:</span>{" "}
                {c.preferred_timezone || "—"}
              </div>
              <div className="text-xs mt-1 text-gray-700">
                <span className="font-medium">Agent:</span>{" "}
                <Truncate text={c.clinic_agent_name} max={18} />
              </div>
              <RatingBadge stats={c.rating_stats} />
            </TableCell>

            <TableCell className="align-top">
              <Truncate text={c.phone} max={16} className="text-sm" />
              <div className="text-xs mt-2 text-gray-700">
                <span className="font-medium">Address:</span>{" "}
                <Truncate text={c.address} max={22} />
              </div>
            </TableCell>

            <TableCell className="align-top">
              <div className="flex flex-col gap-2">
                <StatusPill enabled={!!c.unifi_scraping_enabled} />
                <div className="text-xs text-gray-600 break-all">
                  <span className="font-medium">Console:</span>{" "}
                  {c.unifi_console_id ? (
                    <Truncate text={c.unifi_console_id} max={22} />
                  ) : (
                    "—"
                  )}
                </div>
              </div>
            </TableCell>

            <TableCell className="align-top">
              <div className="flex flex-col gap-2">
                <Button
                  color="light"
                  size="xs"
                  onClick={() => onView("Clinic Info", c.clinic_info)}
                >
                  Info
                </Button>
                <Button
                  color="light"
                  size="xs"
                  onClick={() => onView("Welcome Message", c.welcome_message)}
                >
                  Welcome
                </Button>
                <Button
                  color="light"
                  size="xs"
                  onClick={() =>
                    onView(
                      "Agent Questions",
                      JSON.stringify(c.agent_questions || {}, null, 2),
                      true
                    )
                  }
                >
                  Questions
                </Button>
              </div>
            </TableCell>

            <TableCell className="align-top">
              <div className="flex flex-col gap-2">
                <Button size="sm" onClick={() => onEdit(c)} className="w-full">
                  Edit
                </Button>
                <Button
                  size="sm"
                  color="purple"
                  onClick={() => onReport(c)}
                  className="w-full"
                >
                  Report
                </Button>
                {c.owner_user_id && (
                  <Button
                    size="sm"
                    color="amber"
                    onClick={() => onImpersonate(c)}
                    className="w-full"
                  >
                    Login As
                  </Button>
                )}
                {isPmsConfigured(c.active_pms_provider, c.pms_config) && (
                  <Button
                    size="sm"
                    color="green"
                    disabled={setupLoading}
                    onClick={() => onRunSetup(c.id)}
                    className="w-full"
                  >
                    {setupLoading ? "Setting up…" : "Run Setup"}
                  </Button>
                )}
              </div>
            </TableCell>
          </TableRow>
          );
        })}

        {clinics.length === 0 && (
          <TableRow>
            <TableCell colSpan={5} className="text-center py-8">
              <div className="text-gray-500">No clinics match your search.</div>
            </TableCell>
          </TableRow>
        )}
      </TableBody>
    </Table>
  );
}
